import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Chat,
  Channel,
  Window,
  MessageInput,
  VirtualizedMessageList,
  CustomMessageActions,
} from 'stream-chat-react';
import {
  ContextualChatContext,
  LeftSidePanelPageType,
  RightSidePanelPageType,
} from 'contexts';
import styled from 'styled-components';
import { Button, Icon, Text } from 'components';
import Loading from '@app/react-common/Loading';
import { useBreakpoint } from 'hooks';
import { Modal } from '../../components/Modal';
import {
  ChannelListPanel,
  CustomChannelHeader,
  NewChatPanel,
  ListingCentricActions,
  LoadingListingChat,
  StartAConversationEmptyState,
  SideDetailsPanel,
  SideContactDetailsPanel,
  SideMediaLinkDocDetailsPanel,
  SideSearchInChatPanel,
  CustomChannelInput,
  CustomMessageTime,
  CustomMessageDate,
} from '../../app/ContextualChat';
import '../../styles/streamchat.css';

interface IContextualWrapper {
  setHighlightedId: Dispatch<SetStateAction<string>>;
  highlightedId?: string;
}

const CHANNEL_BUFFER = 385;
const DETAILS_BUFFER = 300;
const EXPANDED_BUFFER = 165;
const CHAT_BUFFER = 430;

const ContextualChatModal = () => {
  const { md, lg } = useBreakpoint();
  const {
    client,
    channel,
    chatModalVisible,
    creatingChatLoading,
    setChatModalVisible,
    leftSidePanelPage,
    rightSidePanelPage,
    viewDetailsPanel,
    hasConnectionError,
    retryConnection,
    isConnecting,
    highlightedId,
    setHighlightedId,
  } = useContext(ContextualChatContext);

  useEffect(() => {
    if (chatModalVisible && channel) {
      channel.watch();
    }
  }, [chatModalVisible, channel]);

  const getSidePanelContent = useCallback(() => {
    if (!viewDetailsPanel) {
      return;
    }

    const getInternal = () => {
      switch (rightSidePanelPage) {
        case RightSidePanelPageType.SearchInChat:
          return (
            <SideSearchInChatPanel
              setHighlightedId={id => setHighlightedId(id)}
            />
          );
        case RightSidePanelPageType.MediaLinkDocDetails:
          return <SideMediaLinkDocDetailsPanel />;
        case RightSidePanelPageType.ContactDetails:
          return <SideContactDetailsPanel />;
        default:
          return <SideDetailsPanel />;
      }
    };

    return !md ? <StyledPusher>{getInternal()}</StyledPusher> : getInternal();
  }, [md, rightSidePanelPage, viewDetailsPanel]);

  const getChannelListElement = () => (
    <>
      {leftSidePanelPage === LeftSidePanelPageType.ChannelList && (
        <ChannelListPanel setHighlightedId={id => setHighlightedId(id)} />
      )}
      {leftSidePanelPage === LeftSidePanelPageType.NewChat && <NewChatPanel />}
    </>
  );

  const getChannelWrapper = (children: ReactNode) => (
    <Channel
      key={channel?.cid}
      channel={channel}
      EmptyPlaceholder={<StartAConversationEmptyState />}
      LoadingIndicator={() => <Loading />}
      MessageStatus={() => null}
      MessageTimestamp={CustomMessageTime}
      DateSeparator={CustomMessageDate}
      reactionOptions={[
        { type: 'love', Component: () => <>❤️</> },
        { type: 'like', Component: () => <>👍</> },
        { type: 'sad', Component: () => <>👎</> },
        { type: 'haha', Component: () => <>😂</> },
        { type: 'wow', Component: () => <>❗</> },
      ]}
    >
      {children}
    </Channel>
  );

  const contextualWrapper = useMemo(
    () => (
      <ContextualWrapper
        setHighlightedId={setHighlightedId}
        highlightedId={highlightedId}
      />
    ),
    [highlightedId],
  );

  const getChannelElement = (showPanelContent?: boolean) => (
    <RightPanelContainer>
      {creatingChatLoading && <LoadingListingChat />}
      {leftSidePanelPage === LeftSidePanelPageType.ChannelList &&
        !creatingChatLoading &&
        getChannelWrapper(
          <>
            {contextualWrapper}
            {showPanelContent ? getSidePanelContent() : null}
          </>,
        )}

      {leftSidePanelPage === LeftSidePanelPageType.NewChat &&
        !creatingChatLoading && <StartAConversationEmptyState />}
    </RightPanelContainer>
  );

  const renderChatLayout = (content: ReactNode) => (
    <Chat client={client}>{content}</Chat>
  );

  const renderDynamicLayout = () => {
    if (lg) {
      return renderChatLayout(
        <>
          {getChannelListElement()}
          {getChannelElement(true)}
        </>,
      );
    }

    if (md) {
      return renderChatLayout(
        viewDetailsPanel ? (
          getChannelElement(true)
        ) : (
          <>
            {getChannelListElement()}
            {getChannelElement(false)}
          </>
        ),
      );
    }

    if (!channel) {
      return renderChatLayout(getChannelListElement());
    }

    return renderChatLayout(
      viewDetailsPanel
        ? getChannelWrapper(getSidePanelContent())
        : getChannelElement(!!viewDetailsPanel),
    );
  };

  const closeModal = useCallback(() => {
    setChatModalVisible(false);
    if (channel) {
      channel.stopWatching();
    }
  }, [channel, setChatModalVisible]);

  return (
    <Modal
      open={chatModalVisible}
      centered
      closable={false}
      footer={null}
      keyboard
      width={1100}
      style={{ overflow: 'hidden' }}
      bodyStyle={{
        padding: 0,
        margin: 'auto',
        maxHeight: '80dvh',
        maxWidth: '1100px',
        display: 'flex',
        flexDirection: 'column',
      }}
      onCancel={closeModal}
    >
      {isConnecting ? (
        <Loading size="fa-2x" />
      ) : (
        <>
          {hasConnectionError || !client ? (
            <RetryContainer>
              <Text>Sorry, we were unable to get your messages.</Text>
              <Button onClick={retryConnection}>Retry connection</Button>
            </RetryContainer>
          ) : (
            <>
              <StyledCloseContainer>
                <Icon type="antdCloseOutlined" onClick={closeModal} />
              </StyledCloseContainer>
              <StyledDynamicContainer id="chat-window-content">
                {renderDynamicLayout()}
              </StyledDynamicContainer>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

// This extraction is needed to be able to invoke useChannelActionContext hook, must be a child
const ContextualWrapper = ({
  setHighlightedId,
  highlightedId,
}: IContextualWrapper) => {
  const { md, lg } = useBreakpoint();
  const { viewDetailsPanel, expandedChannel, setQuotedMessage } = useContext(
    ContextualChatContext,
  );
  const [maxWidth, setMaxWidth] = useState<number>();

  useEffect(() => {
    const parent = document.getElementById('chat-window-content');
    if (!parent) return;

    const observer = new ResizeObserver(entries => {
      setMaxWidth(entries[0].contentRect.width);
    });
    observer.observe(parent);

    return () => observer.disconnect();
  }, []);

  const calcMaxWidth = useMemo(() => {
    if (!md) {
      return maxWidth;
    }

    return (
      maxWidth -
      (expandedChannel ? CHANNEL_BUFFER : EXPANDED_BUFFER) -
      (viewDetailsPanel ? DETAILS_BUFFER : 0)
    );
  }, [md, maxWidth, expandedChannel, viewDetailsPanel]);

  const customMessageActions: CustomMessageActions = {
    Reply: message => setQuotedMessage(message),
  };

  return (
    <StyledFlexContainer
      maxWidth={calcMaxWidth}
      minWidth={!lg && md && viewDetailsPanel ? CHAT_BUFFER : undefined}
    >
      <Window>
        <CustomChannelHeader />
        <ListingCentricActions />
        <VirtualizedMessageList
          customMessageActions={customMessageActions}
          messageActions={[
            'react',
            'markUnread',
            'markRead',
            'edit',
            'delete',
            'reply',
          ]}
          disableDateSeparator={false}
          additionalVirtuosoProps={{
            onScroll: () => setHighlightedId(''),
          }}
          highlightedMessageId={highlightedId ?? undefined}
          shouldGroupByUser
          maxTimeBetweenGroupedMessages={60_000}
        />
        <MessageInput Input={CustomChannelInput} />
      </Window>
    </StyledFlexContainer>
  );
};

const StyledFlexContainer = styled.div<{ maxWidth: number; minWidth?: number }>`
  width: 100%;
  max-width: ${({ maxWidth }) => `${maxWidth}px`};
  min-width: ${({ minWidth }) => (minWidth ? `${minWidth}px` : undefined)};
`;

const StyledDynamicContainer = styled.div`
  display: flex;
  height: calc(100dvh - 236px);
`;

const StyledCloseContainer = styled.div`
  min-height: 42px;
  border-bottom: 1px solid #e3e3e3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 16px;
`;

const StyledPusher = styled.div`
  width: calc(100vw - 16px);
  display: flex;
`;

const RetryContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-direction: column;
  flex: 1;
`;

const RightPanelContainer = styled.div`
  flex: 1;
`;

export default ContextualChatModal;
